<template>
  <label
    class="u-block u-textBlackAlt u-spaceBottom04"
    :class="labelClass"
    :for="props.id"
    v-if="props.label"
  >
    {{ props.label }}
    <span v-if="props.required"
      ><span class="u-hiddenVisually">(required)</span><sup class="u-textRed">*</sup></span
    >
    <span v-else class="u-hiddenVisually">(optional)</span>
  </label>
  <select
    :class="`Select ${props.selectClass} ${props.required ? 'is-required' : ''} ${errorMessage ? 'u-borderColorRed' : ''}`"
    :id="props.id"
    :name="props.name"
    :required="props.required"
    :value="currentValue"
    @change="handleInput"
    @blur="handleBlur"
    :aria-describedby="props.id + 'Help'"
    :disabled="props.disabled || false"
    v-bind="$attrs"
  >
    <option value="" disabled v-if="props.placeholder">{{ props.placeholder }}</option>

    <!-- Option groups support -->
    <template v-if="hasOptionGroups">
      <optgroup v-for="group in options" :key="group.label" :label="group.label">
        <option
          v-for="option in group.options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
          :disabled="option.disabled"
        >
          {{ getOptionLabel(option) }}
        </option>
      </optgroup>
    </template>

    <!-- Regular options (no groups) -->
    <template v-else>
      <option
        v-for="option in options"
        :key="getOptionValue(option)"
        :value="getOptionValue(option)"
        :disabled="option.disabled"
      >
        {{ getOptionLabel(option) }}
      </option>
    </template>
  </select>
  <!-- Help text for additional context -->
  <small
    class="u-block u-textShrink2 u-textGrey u-spaceTop01"
    v-if="props.helpText"
    :id="props.id + 'Help'"
    >{{ props.helpText }}</small
  >
  <!-- Validation error message -->
  <p class="u-textShrink1 u-textRed u-spaceTop02" v-if="errorMessage" role="alert">
    {{ errorMessage }}
  </p>
</template>

<script setup>
/**
 * BaseSelect Component
 *
 * A reusable select dropdown component with built-in validation using vee-validate.
 * Supports flexible option formats and customizable validation rules.
 *
 * Usage Examples:
 *
 * // Simple array of strings
 * <BaseSelect
 *   id="fruits"
 *   label="Favorite Fruit"
 *   :options="['Apple', 'Orange', 'Banana']"
 *   v-model="selectedFruit"
 * />
 *
 * // Array of objects with value/label
 * <BaseSelect
 *   id="states"
 *   label="State"
 *   :options="[{value: 'CA', label: 'California'}, {value: 'NY', label: 'New York'}]"
 *   option-value="value"
 *   option-label="label"
 *   v-model="selectedState"
 * />
 *
 * // Custom validation
 * <BaseSelect
 *   id="priority"
 *   label="Priority Level"
 *   :options="priorityLevels"
 *   :custom-rules="[(value) => value !== 'low' || 'High priority required']"
 *   required
 *   v-model="priority"
 * />
 */

// Prevent attribute inheritance to manually control where they're applied
import { useAttrs, computed, watch, onMounted } from 'vue'
import { useField } from 'vee-validate'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = defineProps({
  // The current value of the select (used with v-model)
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  // Unique identifier for the select (required for accessibility)
  id: {
    type: String,
    required: true,
  },
  // Form field name (defaults to id if not provided)
  name: {
    type: String,
    default: '',
  },
  // Visible label text above the select
  label: {
    type: String,
    default: '',
  },
  // Placeholder text for the default disabled option
  placeholder: {
    type: String,
    default: 'Select an option',
  },
  // Additional help text shown below the select
  helpText: {
    type: String,
    default: '',
  },
  // Whether the field is required for form submission
  required: {
    type: Boolean,
    default: false,
  },
  // Disabled attribute to disable the select
  disabled: {
    type: Boolean,
    default: false,
  },
  // Additional CSS classes for the select element
  selectClass: {
    type: String,
    default: '',
  },
  labelClass: {
    type: String,
    default: '',
  },
  // Array of options - can be strings, numbers, or objects
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  // Property name to use as option value (for object options)
  optionValue: {
    type: String,
    default: 'value',
  },
  // Property name to use as option label (for object options)
  optionLabel: {
    type: String,
    default: 'label',
  },
  // Custom validation rules (array of functions)
  customRules: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['update:modelValue', 'blur'])

/**
 * Helper function to get option value
 * Handles both primitive values and objects
 */
const getOptionValue = (option) => {
  if (typeof option === 'object' && option !== null) {
    return option[props.optionValue] !== undefined ? option[props.optionValue] : option.value
  }
  return option
}

/**
 * Helper function to get option label
 * Handles both primitive values and objects
 */
const getOptionLabel = (option) => {
  if (typeof option === 'object' && option !== null) {
    return option[props.optionLabel] !== undefined
      ? option[props.optionLabel]
      : option.label || option.name || option.value
  }
  return option
}

/**
 * Computed property to detect if options are grouped
 * Option groups should have 'label' and 'options' properties
 */
const hasOptionGroups = computed(() => {
  return (
    props.options.length > 0 &&
    props.options[0] &&
    typeof props.options[0] === 'object' &&
    'label' in props.options[0] &&
    'options' in props.options[0] &&
    Array.isArray(props.options[0].options)
  )
})

/**
 * Validation Rules Generator
 * Creates vee-validate rules based on the component props
 */
const validationRules = computed(() => {
  const rules = []

  // Required field validation
  if (props.required) {
    rules.push((value) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        return 'This field is required'
      }
      return true
    })
  }

  // Add custom validation rules
  rules.push(...props.customRules)

  return rules
})

/**
 * Vee-Validate Field Setup
 * Integrates the select with vee-validate for form validation
 */
const {
  errorMessage,
  value: fieldValue,
  handleBlur: veeHandleBlur,
  handleChange,
  validate,
} = useField(props.name || props.id, validationRules, {
  initialValue: props.modelValue || '',
  validateOnValueUpdate: false,
  validateOnBlur: true,
  validateOnMount: false,
})

/**
 * Input Event Handler
 * Handles user selection and updates both vee-validate and parent component
 */
const handleInput = (event) => {
  const newValue = event.target.value
  handleChange(newValue)
  emit('update:modelValue', newValue)
}

/**
 * Blur Event Handler
 * Triggers validation when user leaves the field
 */
const handleBlur = async (event) => {
  veeHandleBlur(event)
  await validate()
  emit('blur', event)
}

/**
 * Prop-to-Field Synchronization
 * Keeps vee-validate's internal value in sync with the modelValue prop
 */
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== fieldValue.value) {
      handleChange(newValue)
    }
  },
)

/**
 * Current field value for template binding
 */
const currentValue = computed(() => fieldValue.value || props.modelValue)
</script>
